/**
 * <variant-picker>
 *
 * Pill-style single-option variant selector for the product page. Only
 * rendered by sections/main-product.liquid for products that have more than
 * the default variant (i.e. NOT product.has_only_default_variant), so this
 * component never runs for single-variant / WYSIWYG products.
 *
 * Markup contract (all selectors are scoped to the enclosing .main-product):
 *   [data-product-variants]   — <script type="application/json"> of the full
 *                               product.variants array (parsed on init)
 *   [data-variant-options]    — the role="radiogroup" wrapper
 *   [data-variant-pill]       — each option <button role="radio">. Carries:
 *                                 data-variant-id, data-variant-available,
 *                                 data-variant-price (money-formatted),
 *                                 data-variant-sku, data-variant-title,
 *                                 data-variant-media-id (optional)
 *   [data-variant-selected]   — "Selected: …" live-region text
 *
 * Elsewhere in the section (found relative to .main-product):
 *   [data-variant-id-input]   — hidden form input name="id"
 *   [data-product-price]      — price display
 *   [data-product-sku]        — sku display (hidden when empty)
 *   [data-product-stock]      — stock indicator wrapper (+ child
 *                               [data-stock-dot], [data-stock-text], and
 *                               data-text-available / data-text-sold strings)
 *   [data-add-to-cart]        — submit button (+ [data-add-available],
 *                               [data-add-sold], [data-add-price])
 *   [data-payment-buttons]    — dynamic-checkout button wrapper
 *   [data-product-gallery-thumb][data-media-id] — gallery thumbs (clicked to
 *                               switch the featured image, reusing the gallery)
 *
 * On selection it updates the hidden input, price, sku, stock, add-to-cart and
 * featured image, rewrites the URL's ?variant= param via history.replaceState,
 * and dispatches a `variant:changed` CustomEvent with the parsed variant as
 * detail. Sold-out variants are non-selectable (click prevented, skipped in
 * keyboard nav). Mid-session availability changes are handled by Shopify at
 * /cart/add and surfaced by <cart-drawer>.
 */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.main-product') || document;

    this.variants = this._parseVariants();
    this.optionsEl = this.querySelector('[data-variant-options]');
    this.pills = Array.from(this.querySelectorAll('[data-variant-pill]'));
    this.selectedText = this.querySelector('[data-variant-selected]');

    // Section-level targets the picker drives.
    this.idInput = this.section.querySelector('[data-variant-id-input]');
    this.priceEl = this.section.querySelector('[data-product-price]');
    this.skuEl = this.section.querySelector('[data-product-sku]');
    this.stockEl = this.section.querySelector('[data-product-stock]');
    this.addButton = this.section.querySelector('[data-add-to-cart]');
    this.paymentButtons = this.section.querySelector('[data-payment-buttons]');

    if (!this.pills.length) return;

    this.optionsEl.addEventListener('click', this._onClick.bind(this));
    this.optionsEl.addEventListener('keydown', this._onKeydown.bind(this));

    // Prefer the variant named in the URL; fall back to the server-selected
    // pill. (The server already resolves ?variant= into the selected variant,
    // so this is mostly defensive — but it keeps the UI honest if they differ.)
    const params = new URLSearchParams(window.location.search);
    const urlVariantId = params.get('variant');
    let initial =
      (urlVariantId && this.pills.find((p) => p.dataset.variantId === urlVariantId)) ||
      this.pills.find((p) => p.getAttribute('aria-checked') === 'true') ||
      this.pills[0];

    // Sync internal state to the server-rendered selection without rewriting
    // the URL or re-dispatching on load — unless the URL asked for a specific,
    // different variant.
    const silent = !(urlVariantId && initial.dataset.variantId === urlVariantId);
    this._select(initial, { silent });
  }

  _parseVariants() {
    const script = this.querySelector('[data-product-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      console.error('variant-picker: could not parse variant JSON', err);
      return [];
    }
  }

  _isAvailable(pill) {
    return pill.dataset.variantAvailable === 'true';
  }

  _onClick(e) {
    const pill = e.target.closest('[data-variant-pill]');
    if (!pill) return;
    if (!this._isAvailable(pill)) return; // sold-out: click prevented
    this._select(pill);
    pill.focus();
  }

  _onKeydown(e) {
    const current = e.target.closest('[data-variant-pill]');
    if (!current) return;

    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      if (this._isAvailable(current)) this._select(current);
      return;
    }

    let delta = 0;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') delta = 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') delta = -1;
    else return;

    e.preventDefault();
    const next = this._nextAvailable(current, delta);
    if (!next) return;
    this._select(next);
    next.focus();
  }

  // Walk the pills in `delta` direction, skipping unavailable ones, wrapping
  // around. Returns null if no available pill exists (all sold out).
  _nextAvailable(from, delta) {
    const n = this.pills.length;
    const start = this.pills.indexOf(from);
    for (let step = 1; step <= n; step++) {
      const pill = this.pills[(((start + delta * step) % n) + n) % n];
      if (this._isAvailable(pill)) return pill;
    }
    return null;
  }

  _select(pill, { silent = false } = {}) {
    const variantId = pill.dataset.variantId;
    const variant = this.variants.find((v) => String(v.id) === String(variantId));

    this.pills.forEach((p) => {
      const isSel = p === pill;
      p.classList.toggle('is-selected', isSel);
      p.setAttribute('aria-checked', isSel ? 'true' : 'false');
      // Roving tabindex: the selected pill is the group's tab stop; unavailable
      // pills are never tab stops.
      if (!this._isAvailable(p)) p.tabIndex = -1;
      else p.tabIndex = isSel ? 0 : -1;
    });

    this._updateSelectedText(pill);
    this._updateForm(variantId);
    this._updatePrice(pill);
    this._updateSku(pill);
    this._updateStock(pill);
    this._updateAddButton(pill);
    this._updateImage(pill);

    if (!silent) {
      this._updateUrl(variantId);
      this.dispatchEvent(
        new CustomEvent('variant:changed', {
          bubbles: true,
          detail: { variant: variant || null },
        })
      );
    }
  }

  _updateSelectedText(pill) {
    if (!this.selectedText) return;
    const template = this.selectedText.dataset.template;
    const title = pill.dataset.variantTitle || '';
    // The server rendered the full "Selected: X" string; swap only the value
    // by re-using its label prefix when present, else fall back to title.
    if (template) {
      this.selectedText.textContent = template.replace('[value]', title);
    } else {
      // Preserve any non-variable prefix (e.g. "Selected: ") from the server
      // render by replacing the trailing value after the last ": " if present.
      const base = this.selectedText.textContent;
      const idx = base.indexOf(': ');
      this.selectedText.textContent = idx > -1 ? base.slice(0, idx + 2) + title : title;
    }
  }

  _updateForm(variantId) {
    if (this.idInput) this.idInput.value = variantId;
  }

  _updatePrice(pill) {
    if (this.priceEl && pill.dataset.variantPrice) {
      this.priceEl.textContent = pill.dataset.variantPrice;
    }
  }

  _updateSku(pill) {
    if (!this.skuEl) return;
    const sku = pill.dataset.variantSku || '';
    this.skuEl.textContent = sku;
    if (sku) this.skuEl.removeAttribute('hidden');
    else this.skuEl.setAttribute('hidden', '');
  }

  _updateStock(pill) {
    if (!this.stockEl) return;
    const available = this._isAvailable(pill);
    const state = available ? 'available' : 'sold';

    this.stockEl.classList.remove('main-product__stock--available', 'main-product__stock--sold', 'main-product__stock--wysiwyg');
    this.stockEl.classList.add('main-product__stock--' + state);

    const dot = this.stockEl.querySelector('[data-stock-dot]');
    if (dot) {
      dot.classList.remove('main-product__stock-dot--available', 'main-product__stock-dot--sold', 'main-product__stock-dot--wysiwyg');
      dot.classList.add('main-product__stock-dot--' + state);
    }

    const text = this.stockEl.querySelector('[data-stock-text]');
    if (text) {
      text.textContent = available
        ? this.stockEl.dataset.textAvailable
        : this.stockEl.dataset.textSold;
    }
  }

  _updateAddButton(pill) {
    if (!this.addButton) return;
    const available = this._isAvailable(pill);

    this.addButton.disabled = !available;
    if (available) this.addButton.removeAttribute('aria-disabled');
    else this.addButton.setAttribute('aria-disabled', 'true');

    const availEl = this.addButton.querySelector('[data-add-available]');
    const soldEl = this.addButton.querySelector('[data-add-sold]');
    if (availEl) availEl.hidden = !available;
    if (soldEl) soldEl.hidden = available;

    const priceEl = this.addButton.querySelector('[data-add-price]');
    if (priceEl && pill.dataset.variantPrice) {
      priceEl.textContent = pill.dataset.variantPrice;
    }

    if (this.paymentButtons) this.paymentButtons.hidden = !available;
  }

  // Reuse the gallery: click the thumb whose media id matches the variant's
  // featured media. Falls back to no-op when the variant has no dedicated
  // image or the gallery only has a single (untumbnailed) image.
  _updateImage(pill) {
    const mediaId = pill.dataset.variantMediaId;
    if (!mediaId) return;
    const thumb = this.section.querySelector(
      '[data-product-gallery-thumb][data-media-id="' + mediaId + '"]'
    );
    if (thumb) thumb.click();
  }

  _updateUrl(variantId) {
    if (!window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
